/**
 * Local data for the contracts pages: repository headline numbers, the
 * per-agreement invoice-compliance record, and the "upload agreement" scenario.
 * Everything is keyed by the shared contract ids.
 */
import type { ScriptedStep } from '../../components/useScriptedSteps';
import { COMPANY } from '../../data/company';
import { getSupplier } from '../../data/suppliers';
import type { ContractId } from '../../data/types';

/** Repository-wide numbers (README canonical figures). */
export const REPOSITORY_STATS = {
  activeAgreements: 1_946,
  clausesExtracted: 41_580,
  /** Renewals and notice deadlines in the next 90 days, whole repository. */
  renewalsIn90Days: 38,
  /** Agreements shown in this demo's table. */
  curatedAgreements: 26,
};

export type InvoiceCheckStatus = 'violation' | 'warning' | 'compliant';

export interface LatestInvoice {
  /** Id used by /procurement/invoice-compliance?invoice= */
  invoiceId: string;
  amount: number;
  status: InvoiceCheckStatus;
  receivedDaysAgo: number;
  /** What the checker found, one line each, with the clause cited. */
  findings: string[];
}

export interface ContractCompliance {
  /** Invoices checked against this agreement, year to date. */
  invoicesChecked: number;
  violationsCaught: number;
  /** Dollars recovered or prevented year to date. */
  recovered: number;
  /** Dollars currently recoverable on open invoices. */
  openRecoverable: number;
  latestInvoice: LatestInvoice | null;
}

const NONE: ContractCompliance = {
  invoicesChecked: 0,
  violationsCaught: 0,
  recovered: 0,
  openRecoverable: 0,
  latestInvoice: null,
};

const HALBROOK_LATEST: LatestInvoice = {
  invoiceId: 'HP-INV-55102',
  amount: 96_330,
  status: 'violation',
  receivedDaysAgo: 2,
  findings: [
    'Senior Partner is not an agreed role: 22h at $1,150 vs $950 Partner rate, $4,400 over (statement of work §4.2)',
    'Expenses 14% of fees vs 10% cap, $3,380 over (statement of work §5.3)',
    'Total billed $468,200 vs $450,000 budget cap; $10,420 still over after the rate and expense overcharges come off (statement of work §5.2)',
  ],
};

export const CONTRACT_COMPLIANCE: Record<ContractId, ContractCompliance> = {
  'MSA-GEN-2025-001': {
    invoicesChecked: 13,
    violationsCaught: 0,
    recovered: 0,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'GAI-2026-0912',
      amount: 30_000,
      status: 'compliant',
      receivedDaysAgo: 8,
      findings: ['Monthly subscription at $30,000, Net 45 matches §4.2'],
    },
  },
  'SA-CORV-2024-017': {
    invoicesChecked: 118,
    violationsCaught: 7,
    recovered: 64_310,
    openRecoverable: 8_848,
    latestInvoice: {
      invoiceId: 'CV-2026-08812',
      amount: 305_900,
      status: 'violation',
      receivedDaysAgo: 3,
      findings: [
        'Terms Net 30 vs Net 45 (§4.2)',
        '70,000 ft at $431.50 per 100 ft vs $424.36 contract price, $4,998 over (§3.3)',
        'Freight $3,850 billed on a contract with freight included (§5.1)',
      ],
    },
  },
  'QA-VITX-2023-004': NONE,
  'SA-VITX-2023-004': {
    invoicesChecked: 74,
    violationsCaught: 2,
    recovered: 12_480,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-LUMN-2025-002': {
    invoicesChecked: 62,
    violationsCaught: 3,
    recovered: 41_900,
    openRecoverable: 82_500,
    latestInvoice: {
      invoiceId: 'LB-26-3391',
      amount: 412_500,
      status: 'violation',
      receivedDaysAgo: 5,
      findings: [
        'Tariff surcharge 25% ($82,500) billed although the supplier pays duties and tariffs (§5.1, §5.3)',
      ],
    },
  },
  'SA-AURE-2024-009': {
    invoicesChecked: 131,
    violationsCaught: 4,
    recovered: 9_240,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'AFP-90233',
      amount: 186_400,
      status: 'compliant',
      receivedDaysAgo: 2,
      findings: [
        '2% early-payment discount (pay within 10 days) available, worth $3,728 (§4.3)',
      ],
    },
  },
  'MSA-HALB-2023-011': {
    invoicesChecked: 38,
    violationsCaught: 5,
    recovered: 31_200,
    openRecoverable: 18_200,
    latestInvoice: HALBROOK_LATEST,
  },
  'SOW-HALB-2026-03': {
    invoicesChecked: 6,
    violationsCaught: 2,
    recovered: 6_800,
    openRecoverable: 18_200,
    latestInvoice: HALBROOK_LATEST,
  },
  'MSA-TRAN-2025-006': {
    invoicesChecked: 3_412,
    violationsCaught: 148,
    recovered: 212_640,
    openRecoverable: 4_030,
    latestInvoice: {
      invoiceId: 'TCF-778120',
      amount: 60_670,
      status: 'violation',
      receivedDaysAgo: 4,
      findings: [
        'Fuel surcharge 24% ($11,520) vs 18% cap, $2,880 over (§5.2)',
        'Waiting time $1,150 billed without signed proof (§5.4)',
      ],
    },
  },
  'EA-STRT-2024-001': {
    invoicesChecked: 9,
    violationsCaught: 2,
    recovered: 100_000,
    openRecoverable: 100_000,
    latestInvoice: {
      invoiceId: 'STR-INV-20931',
      amount: 1_000_000,
      status: 'warning',
      receivedDaysAgo: 6,
      findings: [
        'September uptime 99.81% vs 99.95% promised: 10% credit of $100,000 owed and not applied (§6.2)',
      ],
    },
  },
  'MSA-BRTL-2025-004': {
    invoicesChecked: 14,
    violationsCaught: 1,
    recovered: 9_600,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'BLC-3318',
      amount: 125_000,
      status: 'compliant',
      receivedDaysAgo: 5,
      findings: ['Milestone 2 accepted, billing matches the schedule (§2.1)'],
    },
  },
  'CPA-NSTR-2025-002': {
    invoicesChecked: 184,
    violationsCaught: 6,
    recovered: 11_870,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-HOLL-2024-021': {
    invoicesChecked: 142,
    violationsCaught: 2,
    recovered: 5_120,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-KAIS-2025-003': {
    invoicesChecked: 36,
    violationsCaught: 1,
    recovered: 2_340,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-NGTE-2024-012': {
    invoicesChecked: 98,
    violationsCaught: 3,
    recovered: 7_410,
    openRecoverable: 123_600,
    latestInvoice: {
      invoiceId: 'NGF-22871',
      amount: 64_200,
      status: 'warning',
      receivedDaysAgo: 3,
      findings: [
        'Year-to-date volume passed $8M, so the 1.5% rebate on all purchases is owed and has not been credited (§2.2)',
      ],
    },
  },
  'SA-POLR-2022-008': {
    invoicesChecked: 88,
    violationsCaught: 2,
    recovered: 3_980,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-MERI-2025-001': {
    invoicesChecked: 104,
    violationsCaught: 5,
    recovered: 18_640,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-CLRW-2024-006': {
    invoicesChecked: 52,
    violationsCaught: 1,
    recovered: 1_150,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'SA-STLR-2024-005': {
    invoicesChecked: 61,
    violationsCaught: 2,
    recovered: 4_860,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'MSA-KEYS-2024-010': {
    invoicesChecked: 412,
    violationsCaught: 27,
    recovered: 38_420,
    openRecoverable: 2_460,
    latestInvoice: {
      invoiceId: 'KS-104477',
      amount: 42_318,
      status: 'warning',
      receivedDaysAgo: 4,
      findings: [
        'Markup 38% vs 32% cap, $1,840 over (§2.1)',
        'Overtime without prior approval, $620 (§2.2)',
      ],
    },
  },
  'MSA-CLVW-2024-003': {
    invoicesChecked: 55,
    violationsCaught: 1,
    recovered: 720,
    openRecoverable: 0,
    latestInvoice: null,
  },
  'NDA-KEST-2026-001': NONE,
  'SA-MROS-2025-004': {
    invoicesChecked: 96,
    violationsCaught: 3,
    recovered: 4_180,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'AIG-90412',
      amount: 9_800,
      status: 'compliant',
      receivedDaysAgo: 4,
      findings: [
        'Four schedule prices match (§3.1 to §3.4) and no freight is billed (§5.1)',
        '2% off if paid within 10 days (otherwise Net 30), worth $196 (§4.1)',
      ],
    },
  },
  'SA-BRST-2024-011': {
    invoicesChecked: 74,
    violationsCaught: 4,
    recovered: 2_960,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'BS-22188',
      amount: 4_250,
      status: 'violation',
      receivedDaysAgo: 9,
      findings: [
        'Terms Net 30 vs Net 45 (§4.2), 15 days short. Prices and quantities match, so nothing is recoverable and payment is scheduled on contract terms',
      ],
    },
  },
  'MSA-BRZV-2025-002': {
    invoicesChecked: 58,
    violationsCaught: 2,
    recovered: 1_440,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'BVL-7741',
      amount: 1_320,
      status: 'compliant',
      receivedDaysAgo: 1,
      findings: [
        '$600 crating line is the one allowed extra charge (§4.1), signed work order on file; not on PO-7740',
        'Net 45 terms match (§5.2)',
      ],
    },
  },
  'MSA-SNTL-2025-003': {
    invoicesChecked: 129,
    violationsCaught: 3,
    recovered: 1_850,
    openRecoverable: 0,
    latestInvoice: {
      invoiceId: 'SF-3398',
      amount: 1_200,
      status: 'compliant',
      receivedDaysAgo: 3,
      findings: [
        '$1,200 flat route rate matches (§2.1) and Net 15 terms match (§5.2)',
        'No fuel surcharge or waiting-time charge billed',
      ],
    },
  },
};

// ---------------------------------------------------------------------------
// Upload agreement scenario: Kestrel Drives quality agreement, draft v2.
// ---------------------------------------------------------------------------

export const UPLOAD_FILE = {
  name: 'Kestrel_Quality_Agreement_draft_v2.pdf',
  pages: 14,
  sizeLabel: '1.9 MB',
};

/** The playbook the upload is checked against. */
const UPLOAD_PLAYBOOK = 'Pillar quality-agreement playbook';

export const UPLOAD_STEPS: ScriptedStep[] = [
  {
    label: 'Reading 14 pages',
    detail: 'Layout and text extracted, 3 exhibits detected',
    durationMs: 800,
  },
  {
    label: 'Identifying parties, dates and term',
    detail: `${COMPANY.legalName} and ${getSupplier('sup-kestrel').name}, effective on signature, three-year term`,
    durationMs: 800,
  },
  {
    label: 'Extracting 23 clauses',
    detail:
      'Quality systems, change notice, audits, corrective action, records',
    durationMs: 900,
  },
  {
    label: `Comparing against the ${UPLOAD_PLAYBOOK}`,
    detail: 'Playbook v4.2, 31 checks across the extracted clauses',
    durationMs: 900,
  },
  {
    label: '2 deviations flagged',
    detail: 'Change notification period and corrective action response time',
    durationMs: 600,
  },
];

export interface UploadClause {
  label: string;
  value: string;
  section: string;
  matchesPlaybook: boolean;
}

/** The key clauses shown after extraction (12 of the 23). */
export const UPLOAD_CLAUSES: UploadClause[] = [
  {
    label: 'Quality systems',
    value: 'ISO 9001:2015, UL listing kept current',
    section: '2.1',
    matchesPlaybook: true,
  },
  {
    label: 'Certificate of conformance',
    value: 'With every lot, plus routine test results',
    section: '3.2',
    matchesPlaybook: true,
  },
  {
    label: 'Change notification',
    value: '90 days prior notice',
    section: '6.2',
    matchesPlaybook: false,
  },
  {
    label: 'Corrective action response',
    value: '20 business days',
    section: '8.1',
    matchesPlaybook: false,
  },
  {
    label: 'Audit rights',
    value: 'Annual, plus 48 hours for cause',
    section: '5.1',
    matchesPlaybook: true,
  },
  {
    label: 'Failed final test',
    value: 'Notify within 24 hours',
    section: '4.3',
    matchesPlaybook: true,
  },
  {
    label: 'Rejection of lots',
    value: 'Reject and recover costs',
    section: '7.4',
    matchesPlaybook: true,
  },
  {
    label: 'Disclosure of suppliers’ suppliers',
    value: 'Power modules, capacitors and firmware sources disclosed',
    section: '6.4',
    matchesPlaybook: true,
  },
  {
    label: 'Recall cooperation',
    value: 'Notify within 24 hours',
    section: '9.2',
    matchesPlaybook: true,
  },
  {
    label: 'Record retention',
    value: '7 years',
    section: '10.1',
    matchesPlaybook: true,
  },
  {
    label: 'Term',
    value: '3 years, ending with the supply agreement',
    section: '11.1',
    matchesPlaybook: true,
  },
  {
    label: 'Governing law',
    value: 'Texas',
    section: '12.3',
    matchesPlaybook: true,
  },
];

export interface UploadDeviation {
  id: string;
  label: string;
  section: string;
  found: string;
  playbook: string;
  /** Where Pillar already holds the playbook position in a signed agreement. */
  precedent: ContractId;
  precedentSection: string;
  redline: string;
}

export const UPLOAD_DEVIATIONS: UploadDeviation[] = [
  {
    id: 'change-notification',
    label: 'Change notification',
    section: '6.2',
    found: '90 days prior notice',
    playbook: '180 days prior notice and Pillar approval',
    precedent: 'QA-VITX-2023-004',
    precedentSection: '7.2',
    redline:
      'Replace “ninety (90) days” with “one hundred eighty (180) days” and add that Kestrel may not implement a change of factory, subcontractor or component source until Pillar has approved it in writing.',
  },
  {
    id: 'scar-response',
    label: 'Corrective action response',
    section: '8.1',
    found: '20 business days',
    playbook: '10 business days',
    precedent: 'QA-VITX-2023-004',
    precedentSection: '9.1',
    redline:
      'Replace “twenty (20) business days” with “ten (10) business days”, with acknowledgement within two (2) business days and an interim response where the investigation cannot be completed in time.',
  },
];

export const UPLOAD_TOTAL_CLAUSES = 23;
